import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {generatePDF,generateWord} from '../utils/exportHelpers';

const {FiTrendingUp,FiTrendingDown,FiRefreshCw,FiDownload,FiMail,FiAlertTriangle,FiArrowRight}=FiIcons;

const ResultsScreen=({scorecardData,onRestart,onNavigate})=> {
  const {totalScore,maxScore,strengths,weaknesses,riskFlags}=scorecardData;

  const getScoreData=(percentage)=> {
    if (percentage <=39) {
      return {
        title: 'Refinement Needed',
        description: 'You\'re in Refinement stage — and that\'s good news! This is where structure grows and your message strengthens. Spend 1–2 weeks revising big-picture flow and clarifying your purpose.',
        scripture: {verse: 'The one who calls you is faithful, and he will do it.', reference: '1 Thessalonians 5:24'},
        nextTool: {id: 'action-plan', text: 'Build a Refinement Plan'},
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200'
      };
    }
    if (percentage <=69) {
      return {
        title: 'Reader-Ready Draft',
        description: 'You\'re at Reader-Ready Draft stage! Your foundation is strong. Seek developmental feedback or editing to bring cohesion.',
        scripture: {verse: 'Commit to the Lord whatever you do, and he will establish your plans.', reference: 'Proverbs 16:3'},
        nextTool: {id: 'publishing', text: 'Explore Your Publishing Path'},
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200'
      };
    }
    if (percentage <=85) {
      return {
        title: 'Polish & Prepare',
        description: 'You\'re in Polish & Prepare zone — only refinement and proofreading remain. Plan your editing pass and start exploring publishing options.',
        scripture: {verse: 'Let us not become weary in doing good, for at the proper time we will reap a harvest if we do not give up.', reference: 'Galatians 6:9'},
        nextTool: {id: 'publishing', text: 'Select Your Publishing Path'},
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200'
      };
    }
    return {
      title: 'Publish-Ready Manuscript',
      description: 'Congratulations — your manuscript is Publish-Ready! Time to format, design, and launch.',
      scripture: {verse: 'May the favor of the Lord our God rest on us; establish the work of our hands for us—yes, establish the work of our hands.', reference: 'Psalm 90:17'},
      nextTool: {id: 'publishing', text: 'Finalize Your Publishing Path'},
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-200'
    };
  };

  const percentage=Math.round((totalScore / maxScore) * 100);
  const scoreData=getScoreData(percentage);

  const handleExport=async (format)=> {
    const content={
      title: 'Manuscript Readiness Scorecard Results',
      score: totalScore,
      maxScore: maxScore,
      percentage: percentage,
      category: scoreData.title,
      description: scoreData.description,
      strengths: strengths,
      weaknesses: weaknesses,
      riskFlags: riskFlags,
      scripture: scoreData.scripture,
      date: new Date().toLocaleDateString()
    };
    if (format==='pdf') {
      await generatePDF(content,'manuscript-readiness-results.pdf');
    } else {
      await generateWord(content,'manuscript-readiness-results.docx');
    }
  };

  const handleEmailResults=()=> {
    const subject=encodeURIComponent('My Manuscript Readiness Scorecard Results');
    const body=encodeURIComponent(`
I just completed the GospelWise Manuscript Readiness Scorecard!
My Score: ${totalScore}/${maxScore} (${percentage}%)
Category: ${scoreData.title}

This assessment has given me great clarity on my next steps!
Strengths: ${strengths.map(s=> s.text).join(',')}
Weaknesses: ${weaknesses.map(w=> w.text).join(',')}
    `);
    window.open(`mailto:?subject=${subject}&body=${body}`);
  };

  return (
    <div className="min-h-screen py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <motion.div initial={{opacity: 0,y: -20}} animate={{opacity: 1,y: 0}} className="text-center space-y-4">
          <h1 className="text-4xl font-serif text-gray-900">Your Manuscript Assessment</h1>
          <p className="text-lg text-gray-600">Here's where your manuscript stands and your path forward</p>
        </motion.div>

        {/* Score Display */}
        <motion.div initial={{opacity: 0,scale: 0.9}} animate={{opacity: 1,scale: 1}} transition={{delay: 0.2}} className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
            <motion.div 
              className={`h-4 rounded-full ${scoreData.bgColor.replace('bg-','bg-gradient-to-r from-').replace('-50','-400 to-')}${scoreData.color.replace('text-','')}-600`}
              initial={{width: '0%'}}
              animate={{width: `${percentage}%`}}
              transition={{duration: 1.5,delay: 0.5,ease: 'easeOut'}}
            />
          </div>
          <div className="flex justify-between items-end">
            <div className="text-left">
              <div className="text-4xl font-bold text-gray-900">{totalScore}<span className="text-2xl text-gray-500">/{maxScore}</span></div>
              <div className="text-lg font-medium text-gray-600">{percentage}% Readiness</div>
            </div>
            <div className="text-right">
              <h2 className={`text-3xl font-serif ${scoreData.color}`}>{scoreData.title}</h2>
            </div>
          </div>
        </motion.div>

        {/* Results Content */}
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left Column */}
          <motion.div initial={{opacity: 0,x: -20}} animate={{opacity: 1,x: 0}} transition={{delay: 0.4}} className="space-y-6">
            <div className={`bg-white rounded-2xl shadow-lg p-6`}>
              <p className="text-gray-700 leading-relaxed mb-4">{scoreData.description}</p>
              <blockquote className={`p-4 rounded-xl ${scoreData.bgColor} border ${scoreData.borderColor}`}>
                <p className="text-gray-700 italic">"{scoreData.scripture.verse}"</p>
                <cite className={`block text-right font-semibold ${scoreData.color}`}>— {scoreData.scripture.reference}</cite>
              </blockquote>
            </div>

            {riskFlags.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <SafeIcon icon={FiAlertTriangle} className="text-2xl text-yellow-600" />
                  <h3 className="text-xl font-semibold text-gray-900">Potential Risk Flags</h3>
                </div>
                <ul className="space-y-2">
                  {riskFlags.map((flag,index)=> (
                    <li key={index} className="flex items-center gap-3 text-sm text-gray-700 bg-yellow-50 p-2 rounded-md">
                      <SafeIcon icon={FiAlertTriangle} className="text-yellow-500" />
                      <span><strong>{flag.text}</strong> score is below 3, suggesting a need for focused attention.</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </motion.div>

          {/* Right Column */}
          <motion.div initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} transition={{delay: 0.6}} className="space-y-6">
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <SafeIcon icon={FiTrendingUp} className="text-green-500 text-xl" />
                    <h4 className="font-semibold text-gray-800">Top 2 Strengths</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {strengths.map(s=> <li key={s.text}>• {s.text}</li>)}
                  </ul>
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <SafeIcon icon={FiTrendingDown} className="text-orange-500 text-xl" />
                    <h4 className="font-semibold text-gray-800">Top 2 Weaknesses</h4>
                  </div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {weaknesses.map(w=> <li key={w.text}>• {w.text}</li>)}
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Save & Share Your Results</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={()=> handleExport('pdf')} className="flex items-center justify-center gap-2 px-4 py-3 bg-primary hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-300">
                    <SafeIcon icon={FiDownload} /> PDF Report
                  </button>
                  <button onClick={()=> handleExport('word')} className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-900 text-white rounded-lg font-medium transition-all duration-300">
                    <SafeIcon icon={FiDownload} /> Word Doc
                  </button>
                </div>
                <button onClick={handleEmailResults} className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all duration-300">
                  <SafeIcon icon={FiMail} /> Email My Results
                </button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Next Step & Restart */}
        <motion.div initial={{opacity: 0,y: 20}} animate={{opacity: 1,y: 0}} transition={{delay: 0.8}} className="text-center space-y-4">
          <button onClick={()=> onNavigate(scoreData.nextTool.id)} className="inline-flex items-center gap-3 px-8 py-4 bg-accent hover:bg-orange-600 text-white rounded-lg font-medium transition-all duration-300 shadow-lg hover:shadow-xl text-lg">
            Next Step: {scoreData.nextTool.text}
            <SafeIcon icon={FiArrowRight} />
          </button>
          <div className="pt-4">
            <button onClick={onRestart} className="inline-flex items-center gap-2 text-gray-600 hover:text-primary font-medium">
              <SafeIcon icon={FiRefreshCw} />
              Back to Tools
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ResultsScreen;